import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBookingForm } from '../hooks/useBookingForm';
import type { BookingFields, BookingRequest } from '../types/booking';
import type { Lang, ScheduleOption } from '../types/copy';
interface BookingFormProps {
  request: BookingRequest | null;
}
const SCHEDULES: ScheduleOption[] = ['morning', 'afternoon', 'evening', 'flexible'];
const LANGS: Lang[] = ['en', 'es'];
const inputBase = 'w-full rounded-xl border bg-white/[0.04] px-4 text-[16px] text-cream placeholder:text-cream/40 outline-none transition-[border-color,box-shadow] duration-200 focus:border-violet-light focus:shadow-[0_0_0_4px_rgba(196,181,253,0.15)]';
export function BookingForm({
  request
}: BookingFormProps) {
  const {
    t,
    lang
  } = useLanguage();
  const f = t.contact.form;
  const {
    fields,
    errors,
    status,
    update,
    submit,
    reset
  } = useBookingForm(request, lang);
  const borderFor = (invalid?: boolean) => invalid ? 'border-[#FCA5A5]' : 'border-white/15';
  return <div className="rounded-[28px] bg-ink p-7 md:p-10" aria-live="polite" data-id="element-5" data-mp="u9qyj7ew6tf0">
      <AnimatePresence mode="wait" initial={false} data-id="element-6" data-mp="pcqt8ypv9uoy">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex min-h-[420px] flex-col items-start justify-center" data-id="element-7" data-mp="kdxnjsgtn7zc">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage text-ink" data-id="element-8" data-mp="188qnkg6bhabp">
              <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-9" data-mp="139npjfor84d8" />
            </span>
            <h3 className="mt-6 font-display text-[26px] font-medium text-white" data-id="element-10" data-mp="20sfenlnh2lnn">{f.successTitle}</h3>
            <p className="mt-3 max-w-md text-[16.5px] text-cream/75" data-id="element-11" data-mp="1vz34ggjjihef">{f.successBody}</p>
            <button type="button" onClick={reset} className="btn btn-ghost-light mt-8" data-id="element-12" data-mp="3q8rr0s03xrej">
              {f.another}
            </button>
          </motion.div> : <motion.form key="form" noValidate onSubmit={submit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="grid gap-6" data-id="element-13" data-mp="3gj87dk4kltim">
            <Field id="booking-name" label={f.name} error={errors.name ? f.errors.name : undefined} data-id="element-14" data-mp="3bjlsz7d0a788">
              <input id="booking-name" type="text" autoComplete="name" required value={fields.name} onChange={e => update('name', e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'booking-name-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.name)}`} data-id="element-15" data-mp="2xkdpi29t8cy8" />
            </Field>

            <div className="grid gap-6 md:grid-cols-2" data-id="element-16" data-mp="2gas0mv8cjb9w">
              <Field id="booking-email" label={f.email} error={errors.email ? f.errors.email : undefined} data-id="element-17" data-mp="2g85i60jx3bcw">
                <input id="booking-email" type="email" autoComplete="email" required value={fields.email} onChange={e => update('email', e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'booking-email-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.email)}`} data-id="element-18" data-mp="2v18y63hv1h1i" />
              </Field>
              <Field id="booking-phone" label={f.phone} error={errors.phone ? f.errors.phone : undefined} data-id="element-19" data-mp="1jjzkxypbrl8n">
                <input id="booking-phone" type="tel" autoComplete="tel" required value={fields.phone} onChange={e => update('phone', e.target.value)} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'booking-phone-error' : undefined} className={`${inputBase} h-14 ${borderFor(errors.phone)}`} data-id="element-20" data-mp="1el635u30982o" />
              </Field>
            </div>

            <Field id="booking-service" label={f.service} error={errors.service ? f.errors.service : undefined} data-id="element-21" data-mp="3j3x90z8tfpdu">
              <div className="relative" data-id="element-22" data-mp="3s3spnjl54cl9">
                <select id="booking-service" required value={fields.service} onChange={e => update('service', e.target.value as BookingFields['service'])} aria-invalid={!!errors.service} aria-describedby={errors.service ? 'booking-service-error' : undefined} className={`${inputBase} h-14 appearance-none pr-12 ${borderFor(errors.service)} ${fields.service ? '' : 'text-cream/50'}`} data-id="element-23" data-mp="3s2r1qej0zvm4">
                  <option value="" disabled className="text-ink" data-id="element-24" data-mp="2nt91zqjh9xjv">
                    {f.servicePlaceholder}
                  </option>
                  <optgroup label={t.services.title} className="text-ink" data-id="element-25" data-mp="2iu5xc77ettcf">
                    {t.services.items.map(s => <option key={s.id} value={s.id} className="text-ink" data-id="element-26" data-mp="2dxfjzrmzpbup">
                        {s.title}
                      </option>)}
                  </optgroup>
                  <optgroup label={t.memberships.title} className="text-ink" data-id="element-27" data-mp="243oymvm5thj8">
                    {t.memberships.tiers.map(m => <option key={m.id} value={m.id} className="text-ink" data-id="element-28" data-mp="1v5mp1rod1gkc">
                        {m.name} — {m.term}
                      </option>)}
                  </optgroup>
                  <option value="other" className="text-ink" data-id="element-29" data-mp="1ghv3bv7qdp4d">
                    {f.other}
                  </option>
                </select>
                <ChevronDownIcon aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cream/60" data-id="element-30" data-mp="1lj295oqbce8e" />
              </div>
            </Field>

            <div className="grid gap-6 md:grid-cols-[1fr_auto]" data-id="element-31" data-mp="1z8w2aj8upknx">
              <PillGroup legend={f.schedule} name="schedule" options={SCHEDULES.map(s => ({
            value: s,
            label: f.scheduleOptions[s]
          }))} value={fields.schedule} onChange={v => update('schedule', v as ScheduleOption)} data-id="element-32" data-mp="1z9nt7gri6u7v" />
              <PillGroup legend={f.language} name="language" options={LANGS.map(l => ({
            value: l,
            label: f.languageOptions[l]
          }))} value={fields.language} onChange={v => update('language', v as Lang)} data-id="element-33" data-mp="1uft1pr5npay8" />
            </div>

            <div data-id="element-34" data-mp="6ko8s4de4e33">
              <label htmlFor="booking-message" className="flex items-baseline gap-2 text-[14px] font-medium text-cream" data-id="element-35" data-mp="6l6fjuxoiap9">
                {f.message}
                <span className="text-[13px] font-normal text-cream/50" data-id="element-36" data-mp="lbdmbdebq5a4">{f.optional}</span>
              </label>
              <textarea id="booking-message" rows={4} value={fields.message} onChange={e => update('message', e.target.value)} placeholder={f.messagePlaceholder} className={`${inputBase} mt-2 resize-none border-white/15 py-3.5 leading-[1.6]`} data-id="element-37" data-mp="gckarqilkocl" />
            </div>

            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-38" data-mp="3tnt02iw2x5j7">
              <p className="text-[13px] text-cream/55" data-id="element-39" data-mp="1cynlvjf5r5he">{f.privacyNote}</p>
              <button type="submit" disabled={status === 'submitting'} className="btn btn-primary disabled:cursor-wait disabled:opacity-80" data-id="element-40" data-mp="18y3msab0xrsm">
                {status === 'submitting' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-41" data-mp="3cf8kpbvzgiah" />
                    {f.submitting}
                  </> : f.submit}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
function Field({
  id,
  label,
  error,
  children
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-42" data-mp="3v4f4714lpdxc">
      <label htmlFor={id} className="text-[14px] font-medium text-cream" data-id="element-43" data-mp="3zow4q69bo1r">
        {label}
      </label>
      <div className="mt-2" data-id="element-44" data-mp="20sicd728y61r">{children}</div>
      {error && <p id={`${id}-error`} className="mt-2 text-[13px] text-[#FCA5A5]" data-id="element-45" data-mp="20ubkkqsicybv">
          {error}
        </p>}
    </div>;
}
function PillGroup({
  legend,
  name,
  options,
  value,
  onChange
}: {
  legend: string;
  name: string;
  options: {
    value: string;
    label: string;
  }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return <fieldset data-id="element-46" data-mp="e33vgzjybqgg">
      <legend className="text-[14px] font-medium text-cream" data-id="element-47" data-mp="e4ovh4p1c1lp">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2" data-id="element-48" data-mp="95lohephvlb3">
        {options.map(o => <label key={o.value} className="cursor-pointer" data-id="element-49" data-mp="15utd4vleuaj8">
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} className="peer sr-only" data-id="element-50" data-mp="15ua0nuej4ecl" />
            <span className="inline-flex h-11 items-center whitespace-nowrap rounded-full border border-white/15 px-4 text-[14px] text-cream/80 transition-colors duration-150 hover:border-white/40 peer-checked:border-cream peer-checked:bg-cream peer-checked:text-ink peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-violet-light" data-id="element-51" data-mp="rvtm5hafdjjw">
              {o.label}
            </span>
          </label>)}
      </div>
    </fieldset>;
}